/**
 * Vector2 — a tiny, allocation-conscious 2D vector.
 *
 * Boids and drone steering run every fixed tick for potentially hundreds of
 * entities, so we favour in-place ("self") mutation to avoid GC churn on mobile.
 * Pure helpers (add/sub/scale) return NEW vectors for readable math; the
 * `*Self` variants mutate `this` for hot loops.
 */
export class Vector2 {
  constructor(
    public x = 0,
    public y = 0,
  ) {}

  // ---- construction helpers ----
  static zero(): Vector2 {
    return new Vector2(0, 0);
  }
  static fromAngle(rad: number, len = 1): Vector2 {
    return new Vector2(Math.cos(rad) * len, Math.sin(rad) * len);
  }
  clone(): Vector2 {
    return new Vector2(this.x, this.y);
  }
  set(x: number, y: number): this {
    this.x = x;
    this.y = y;
    return this;
  }
  copy(v: Vector2): this {
    this.x = v.x;
    this.y = v.y;
    return this;
  }

  // ---- pure (return new) ----
  add(v: Vector2): Vector2 {
    return new Vector2(this.x + v.x, this.y + v.y);
  }
  sub(v: Vector2): Vector2 {
    return new Vector2(this.x - v.x, this.y - v.y);
  }
  scale(s: number): Vector2 {
    return new Vector2(this.x * s, this.y * s);
  }

  // ---- in-place (mutate this) ----
  addSelf(v: Vector2): this {
    this.x += v.x;
    this.y += v.y;
    return this;
  }
  subSelf(v: Vector2): this {
    this.x -= v.x;
    this.y -= v.y;
    return this;
  }
  scaleSelf(s: number): this {
    this.x *= s;
    this.y *= s;
    return this;
  }

  // ---- magnitude / normalisation ----
  lengthSq(): number {
    return this.x * this.x + this.y * this.y;
  }
  length(): number {
    return Math.hypot(this.x, this.y);
  }
  /** Normalise in place. Zero-length vectors are left at (0,0). */
  normalizeSelf(): this {
    const len = this.length();
    if (len > 1e-9) {
      this.x /= len;
      this.y /= len;
    }
    return this;
  }
  /** Clamp magnitude to `max` in place (used to cap steering force / speed). */
  limitSelf(max: number): this {
    const lenSq = this.lengthSq();
    if (lenSq > max * max) {
      const len = Math.sqrt(lenSq);
      this.x = (this.x / len) * max;
      this.y = (this.y / len) * max;
    }
    return this;
  }
  /** Set magnitude to exactly `len` (direction preserved). */
  setLengthSelf(len: number): this {
    return this.normalizeSelf().scaleSelf(len);
  }

  // ---- products / metrics ----
  dot(v: Vector2): number {
    return this.x * v.x + this.y * v.y;
  }
  distanceTo(v: Vector2): number {
    return Math.hypot(this.x - v.x, this.y - v.y);
  }
  distanceSqTo(v: Vector2): number {
    const dx = this.x - v.x;
    const dy = this.y - v.y;
    return dx * dx + dy * dy;
  }
  angle(): number {
    return Math.atan2(this.y, this.x);
  }
}
